
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/toast';
import { supabase, apiEndpoints, supportService, SupportMessage } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { validateUserSession, isValidUUID } from '@/lib/utils/uuidValidation';
import { getAnonymousSessionId, getClientInfo } from '@/lib/utils/anonymousSession';

export interface SupportChat {
  id: string;
  user_id: string | null;
  anonymous_session_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  is_anonymous: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  is_resolved: boolean;
}

interface PageContext {
  page: string;
  type?: string;
  category?: string | null;
  location?: string | null;
  query?: string | null;
}

export function useSupportChat() {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [apiFailures, setApiFailures] = useState(0);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [anonymousSessionId, setAnonymousSessionId] = useState<string | null>(null);

  // Get current page context for better AI assistance
  const getCurrentPageContext = (): PageContext => {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    
    const pageContext: PageContext = { page: path };
    
    if (path === '/') {
      pageContext.type = 'homepage';
    } else if (path === '/search') {
      pageContext.type = 'search';
      pageContext.category = searchParams.get('category');
      pageContext.location = searchParams.get('location');
      pageContext.query = searchParams.get('q');
    } else if (path.startsWith('/product/')) {
      pageContext.type = 'product_detail';
    } else if (path === '/create-listing') {
      pageContext.type = 'create_listing';
    } else if (path === '/profile') {
      pageContext.type = 'profile';
    } else if (path === '/messages') {
      pageContext.type = 'messages';
    }
    
    return pageContext;
  };

  // Quick action buttons
  const quickActions = [
    {
      id: 'create_listing',
      text: 'Si të krijoj një shpallje?',
      message: 'Si mund të krijoj një shpallje të re në TreguIm?'
    },
    {
      id: 'upload_photos',
      text: 'Probleme me fotot',
      message: 'Kam probleme me ngarkimin e fotove në shpalljen time.'
    },
    {
      id: 'premium_features',
      text: 'Opsionet Premium',
      message: 'Mund të më shpjegoni opsionet premium dhe si funksionojnë?'
    },
    {
      id: 'messaging',
      text: 'Sistemi i mesazheve',
      message: 'Si funksionon sistemi i mesazheve dhe si të kontaktoj shitësit?'
    },
    {
      id: 'payments',
      text: 'Pagesat dhe siguria',
      message: 'Sa të sigurta janë pagesat në TreguIm dhe çfarë metodash përdorni?'
    },
    {
      id: 'account_help',
      text: 'Probleme me llogarinë',
      message: 'Kam probleme me llogarinë time, mund të më ndihmoni?'
    }
  ];

  // Initialize anonymous session if not authenticated
  useEffect(() => {
    console.log("=== SUPPORT CHAT INIT ===")
    console.log("Is authenticated:", isAuthenticated)
    if (!isAuthenticated) {
      const sessionId = getAnonymousSessionId();
      console.log("Anonymous session ID generated:", sessionId)
      setAnonymousSessionId(sessionId);
    } else {
      console.log("User is authenticated, clearing anonymous session")
      setAnonymousSessionId(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isOpen) return;
    
    const loadOrCreateChat = async () => {
      try {
        console.log("=== LOAD OR CREATE CHAT ===")
        console.log("Is open:", isOpen)
        console.log("Is authenticated:", isAuthenticated)
        console.log("User ID:", user?.id)
        console.log("Anonymous session ID:", anonymousSessionId)
        
        setConnectionError(null);
        
        const userId = isAuthenticated && user ? user.id : null;
        const sessionId = !isAuthenticated ? anonymousSessionId : null;
        
        if (isAuthenticated && user && !validateUserSession(user)) {
          console.error("Invalid user session:", user?.id);
          setConnectionError("Invalid user session. Please log out and log back in.");
          return;
        }
        
        console.log("Looking for existing chat...")
        
        // Try to find existing chat
        const { data: existingChat, error: findError } = await supportService.findExistingChat(userId, sessionId);
        
        if (findError) {
          console.error("Error finding existing chat:", findError);
        }
        
        if (existingChat) {
          console.log("Found existing chat:", existingChat.id);
          setActiveChatId(existingChat.id);
          loadMessages(existingChat.id);
        } else {
          console.log("No existing chat found, creating new chat");
          createNewChat();
        }
      } catch (error) {
        console.error('Error loading chat:', error);
        setConnectionError("Couldn't connect to support service. Please try again later.");
        toast({
          title: 'Gabim',
          description: 'Ndodhi një gabim gjatë ngarkimit të bisedës',
          variant: 'destructive',
        });
      }
    };
    
    // Only proceed if we have either a user or an anonymous session
    if ((isAuthenticated && user) || (!isAuthenticated && anonymousSessionId)) {
      console.log("Prerequisites met, loading chat");
      loadOrCreateChat();
    } else {
      console.log("Prerequisites not met - waiting...");
    }
  }, [isOpen, user, isAuthenticated, anonymousSessionId]);

  const loadMessages = async (chatId: string) => {
    if (!chatId || !isValidUUID(chatId)) {
      console.error("Invalid chat ID:", chatId);
      setConnectionError("Invalid chat ID. Please refresh and try again.");
      return;
    }
    
    try {
      console.log("=== LOADING MESSAGES ===")
      console.log("Chat ID:", chatId)
      setConnectionError(null);
      const { data: chatMessages, error } = await supportService.getChatMessages(chatId, anonymousSessionId);
      
      if (error) {
        console.error("Error loading messages:", error);
        setConnectionError("Couldn't load messages. Please try again later.");
        return;
      }
      
      if (chatMessages) {
        console.log(`Loaded ${chatMessages.length} messages`);
        setMessages(chatMessages as SupportMessage[]);
        
        // Show quick actions if this is a new chat
        if (chatMessages.length <= 1) {
          setShowQuickActions(true);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setConnectionError("Network error while loading messages.");
    }
  };

  const createNewChat = async () => {
    const userId = isAuthenticated && user ? user.id : null;
    const sessionId = !isAuthenticated ? anonymousSessionId : null;
    
    if (isAuthenticated && user && !validateUserSession(user)) {
      console.error("Cannot create chat: invalid user session");
      setConnectionError("Invalid user session. Please log out and log back in.");
      return;
    }
    
    try {
      console.log("=== CREATING NEW CHAT ===")
      console.log("User ID:", userId)
      console.log("Session ID:", sessionId)
      setConnectionError(null);
      
      const clientInfo = getClientInfo();
      const { data: newChat, error } = await supportService.createChat(userId, sessionId, clientInfo);
      
      if (error) {
        console.error("Error creating chat:", error);
        setConnectionError("Couldn't create a new chat. Please try again later.");
        return;
      }
      
      if (newChat) {
        console.log("New chat created with ID:", newChat.id);
        setActiveChatId(newChat.id);
        setMessages([]);
        
        const welcomeMessage = isAuthenticated
          ? 'Mirë se vini në mbështetjen e TreguIm! Unë jam këtu për t\'ju ndihmuar me çdo pyetje rreth platformës sonë. Si mund t\'ju ndihmoj sot?'
          : 'Mirë se vini në mbështetjen e TreguIm! Unë jam këtu për t\'ju ndihmoj. Nëse regjistroheni, mund të ruani historinë e bisedës. Si mund t\'ju ndihmoj sot?';
        
        const messageData = {
          chat_id: newChat.id,
          sender_type: 'assistant' as const,
          content: welcomeMessage,
        };
        
        await supportService.createMessage(messageData, anonymousSessionId);
        console.log("Welcome message added");
        
        loadMessages(newChat.id);
      }
    } catch (error) {
      console.error('Error creating new chat:', error);
      setConnectionError("Network error while creating chat.");
      toast({
        title: 'Gabim',
        description: 'Ndodhi një gabim gjatë krijimit të bisedës së re',
        variant: 'destructive',
      });
    }
  };

  const sendMessage = async (messageText?: string) => {
    const messageContent = messageText || newMessage.trim();
    if (!messageContent || !activeChatId) {
      console.log("Cannot send message: missing content or chat ID");
      return;
    }
    
    const userId = isAuthenticated && user ? user.id : null;
    
    if (isAuthenticated && user && !validateUserSession(user)) {
      console.error("Cannot send message: invalid user session");
      setConnectionError("Invalid user session. Please log out and log back in.");
      return;
    }
    
    if (!isValidUUID(activeChatId)) {
      console.error("Cannot send message: invalid chat ID:", activeChatId);
      setConnectionError("Invalid chat session. Please refresh and try again.");
      return;
    }
    
    setNewMessage('');
    setConnectionError(null);
    setShowQuickActions(false);
    
    console.log("=== SENDING MESSAGE ===")
    console.log("Chat ID:", activeChatId)
    console.log("Message:", messageContent.substring(0, 50) + '...')
    console.log("User type:", isAuthenticated ? 'authenticated' : 'anonymous')
    
    const tempMessage: SupportMessage = {
      id: uuidv4(),
      chat_id: activeChatId,
      sender_type: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
      is_read: true,
    };
    
    setMessages(prev => [...prev, tempMessage]);
    setIsTyping(true);
    
    try {
      const { error: saveError } = await supportService.createMessage({
        chat_id: activeChatId,
        sender_type: 'user',
        content: messageContent
      }, anonymousSessionId);
      
      if (saveError) {
        console.error("Error saving user message:", saveError);
        throw new Error("Couldn't save your message");
      }
      
      try {
        setIsLoading(true);
        setIsRetrying(false);
        
        console.log("=== CALLING AI SERVICE ===")
        console.log("Endpoint:", apiEndpoints.customerSupport)
        console.log("Messages count:", messages.length + 1)
        
        const requestBody = {
          messages: [...messages, tempMessage],
          chatId: activeChatId,
          userId: userId,
          anonymousSessionId: !isAuthenticated ? anonymousSessionId : null,
          currentPage: getCurrentPageContext(),
          userContext: {
            isFirstTime: messages.length <= 1,
            platform: 'treguim.org',
            isAuthenticated,
            isAnonymous: !isAuthenticated
          }
        };

        console.log("Request body prepared:", {
          messagesCount: requestBody.messages.length,
          hasUserId: !!requestBody.userId,
          hasAnonymousSession: !!requestBody.anonymousSessionId,
          chatId: requestBody.chatId
        });

        // Build headers - no Authorization header needed since JWT verification is disabled
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };

        console.log("Making request with headers:", Object.keys(headers));

        const response = await fetch(apiEndpoints.customerSupport, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
        });
        
        console.log("=== AI SERVICE RESPONSE ===")
        console.log("Status:", response.status)
        console.log("Status text:", response.statusText)
        console.log("OK:", response.ok)
        
        if (!response.ok) {
          console.error("API response not OK:", response.status, response.statusText);
          let errorMessage = `Server error (${response.status})`;
          let userMessage = 'Na vjen keq, por ndodhi një gabim. Ju lutemi provoni përsëri.';
          
          try {
            const errorData = await response.json();
            console.error("Error response data:", errorData);
            
            setApiFailures(prev => prev + 1);
            
            // Use specific user messages from the API
            if (errorData.userMessage) {
              userMessage = errorData.userMessage;
            } else if (errorData.errorType === "OpenAIError") {
              userMessage = "Problem me AI shërbimin. Ju lutemi provoni përsëri pas disa sekondash.";
            } else if (errorData.errorType === "AuthorizationError") {
              userMessage = "Problem me autorizimin. Ju lutemi rifreskoni faqen dhe provoni përsëri.";
            } else if (errorData.errorType === "ConfigError") {
              userMessage = "Sistemi nuk është konfiguruar siç duhet. Ju lutemi kontaktoni administratorin.";
            } else if (errorData.errorType === "RateLimitError") {
              userMessage = "Keni tejkaluar limitin e mesazheve. Ju lutemi prisni pak dhe provoni përsëri.";
            } else if (response.status === 401) {
              userMessage = "Problem me autorizimin. Ju lutemi rifreskoni faqen.";
            } else if (response.status === 403) {
              userMessage = "Nuk keni leje për të përdorur këtë shërbim.";
            } else if (response.status >= 500) {
              userMessage = "Probleme me serverin. Ju lutemi provoni më vonë.";
            }
            
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            console.error("Error parsing response:", parseError);
            if (response.status === 401) {
              userMessage = "Problem me autorizimin. Ju lutemi rifreskoni faqen.";
            } else if (response.status >= 500) {
              userMessage = "Probleme me serverin. Ju lutemi provoni më vonë.";
            }
          }
          
          throw new Error(userMessage);
        }
        
        const data = await response.json();
        console.log("AI response received:", { 
          hasReply: !!data.reply, 
          hasError: !!data.error,
          hasWarning: !!data.warning 
        });
        
        setApiFailures(0);
        
        if (data.error) {
          const userMessage = data.userMessage || 'Ka ndodhur një gabim me AI. Ju lutemi provoni përsëri.';
          throw new Error(userMessage);
        }
        
        if (data.reply && data.errorType === "DatabaseError") {
          console.warn("Database error when saving AI response, but we have a reply:", data);
          
          const aiMessage: SupportMessage = {
            id: uuidv4(),
            chat_id: activeChatId,
            sender_type: 'assistant',
            content: data.reply,
            created_at: new Date().toISOString(),
            is_read: true,
          };
          
          setMessages(prev => [...prev, aiMessage]);
          setRetryCount(0);
          return;
        }
        
        // Reload messages to get the latest AI response
        loadMessages(activeChatId);
        setRetryCount(0);
        
      } catch (error: any) {
        console.error('Error getting AI response:', error);
        setRetryCount(prev => prev + 1);
        
        let errorMessage = error.message || 'Na vjen keq, por ndodhi një gabim. ';
        
        if (apiFailures >= 3) {
          errorMessage = 'Duket se ka një problem të vazhdueshëm me shërbimin. Ju lutemi provoni përsëri më vonë ose kontaktoni mbështetjen teknike.';
        } else if (retryCount >= 2) {
          errorMessage = 'Duket se ka problem me shërbimin. Ju lutemi provoni përsëri më vonë.';
        }
        
        // Add error message to chat
        await supportService.createMessage({
          chat_id: activeChatId,
          sender_type: 'assistant',
          content: errorMessage
        }, anonymousSessionId);
        
        if (error.message?.includes('fetch') || error.message?.includes('timed out')) {
          setConnectionError(`Network error: Could not connect to AI service. Please check your connection.`);
        } else {
          setConnectionError(`Service error: ${error.message}`);
        }
        
        loadMessages(activeChatId);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      setConnectionError(`Network error: ${error.message}`);
      toast({
        title: 'Gabim',
        description: 'Ndodhi një gabim gjatë dërgimit të mesazhit',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const retryLastUserMessage = async () => {
    if (!messages.length || !activeChatId) return;
    
    const lastUserMessageIndex = [...messages].reverse().findIndex(m => m.sender_type === 'user');
    if (lastUserMessageIndex === -1) return;
    
    const lastUserMessage = [...messages].reverse()[lastUserMessageIndex];
    
    console.log("Retrying message:", lastUserMessage.content);
    
    setIsRetrying(true);
    setNewMessage(lastUserMessage.content);
    
    await sendMessage();
  };

  const handleQuickAction = (action: typeof quickActions[0]) => {
    console.log("Quick action selected:", action.id);
    sendMessage(action.message);
  };

  const toggleChat = () => {
    console.log("Toggling chat - currently open:", isOpen);
    setIsOpen(prev => !prev);
  };

  return {
    isOpen,
    toggleChat,
    messages,
    newMessage,
    setNewMessage,
    sendMessage,
    isLoading,
    isTyping,
    connectionError,
    retryLastUserMessage,
    isRetrying,
    quickActions,
    showQuickActions,
    handleQuickAction
  };
}
